/**
 * Rebuild stored logo URLs using `VITE_API_BASE_URL` so images still load when the DB
 * contains a different host (e.g. localhost from dev upload, production app URL).
 * External URLs (path does not start with `/api/`) are returned unchanged.
 */
import { legacyApiV1RewritePath } from '@/utils/apiBase'

/** Platform default when tenant has no custom logo (matches login shell). */
export const DEFAULT_BRAND_LOGO = '/tanseq-logo.png'

export function resolveBrandingAssetUrl(stored: string | null | undefined): string | null {
  if (!stored?.trim()) return null
  const s = stored.trim()
  if (s.startsWith('data:')) return s

  let pathnameSearch = ''
  let parsedPath = ''
  let parsedSearch = ''
  try {
    const u =
      s.startsWith('http://') || s.startsWith('https://')
        ? new URL(s)
        : new URL(s, 'http://placeholder.local')
    parsedPath = u.pathname
    parsedSearch = u.search
    pathnameSearch = parsedPath + parsedSearch
  } catch {
    return s
  }

  if (!pathnameSearch.startsWith('/api/')) {
    return s.startsWith('http://') || s.startsWith('https://') ? s : null
  }

  pathnameSearch = legacyApiV1RewritePath(parsedPath) + parsedSearch

  const raw = (import.meta.env.VITE_API_BASE_URL || '').trim()
  let origin = raw.replace(/\/api\/?$/i, '').replace(/\/$/, '')
  if (!origin.startsWith('http://') && !origin.startsWith('https://')) {
    if (typeof window !== 'undefined') {
      origin = window.location.origin
    } else {
      return s
    }
  }

  return `${origin}${pathnameSearch}`
}

/** Anonymous-readable tenant logo endpoint (the API resolves the tenant from the request host). */
export const BRANDING_LOGO_PATH = '/api/v1/public/branding-logo'

/**
 * Public URL for the tenant logo. Use on pages that render before a session exists — the
 * `template-assets` route needs `template:read` and would answer 401/403 there (A9-1).
 */
export function publicBrandingLogoUrl(hostname?: string): string | null {
  const host = (
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')
  )
    .trim()
    .toLowerCase()
  if (!host) return null
  return resolveBrandingAssetUrl(`${BRANDING_LOGO_PATH}?host=${encodeURIComponent(host)}`)
}
